import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import { captureMessageForSentry } from '@/helpers/sentry-helper';

import {
	GetGlobalProviderTitlesDocument,
	GetGlobalProviderTitlesQuery,
	GetGlobalProviderTitlesQueryVariables,
} from '@/pages/graphql/queries/GetGlobalProviderTitles.query';

import { PopularTitlesSorting } from '@/@types/graphql-types';
import { CollectionType } from '@/enums/collection-type';

import { useRouter } from 'vue-router/composables';
import { useQuery } from '@/helpers/composables/useApollo';

import { calculateUrl } from '@/helpers/state-helper';
import { SnowplowContext, SnowplowOldModuleContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { GlobalModuleName } from '@/helpers/composables/useGlobalTitles';
import { ImpressionTrackingEvents } from '@/enums/events';
import { defaultObservableOptions } from '@/mixins/types/impressionTracking';
import { storeToRefs } from 'pinia';
import { useConstantStore } from '@/stores/pinia/useConstantStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'GlobalProviderSpotlight',
  props: {
    country: null,
    language: null,
    provider: null,
    moduleName: null
  },
  setup(__props: any) {

const props = __props as {
	country: string;
	language: string;
	provider: { shortName: string };
	moduleName: GlobalModuleName;
};



const router = useRouter();
const { genresByShortName, subgenresList, providersByShortName } = storeToRefs(useConstantStore());

const queryVariables = computed<GetGlobalProviderTitlesQueryVariables>(
	() =>
		({
			country: props.country,
			language: props.language,
			filter: {
				packages: [props.provider.shortName],
			},
			first: 20,
			sortBy: PopularTitlesSorting.Popular,
		} as GetGlobalProviderTitlesQueryVariables)
);

const { result, onError } = useQuery<GetGlobalProviderTitlesQuery, GetGlobalProviderTitlesQueryVariables>(
	GetGlobalProviderTitlesDocument,
	queryVariables,
	{
		fetchPolicy: 'cache-first',
		errorPolicy: 'all',
	}
);

const globalProviderSpotlightTitles = computed(() => result.value?.popularTitles.edges ?? []);

onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Global Provider Titles Data]:',
		{ error: error, where: 'Component: GlobalProviderSpotlight' },
		'error'
	);
});

const providerName = computed(() => providersByShortName.value[props.provider.shortName].clearName);

const providerUrl = computed(() =>
	calculateUrl(
		router,
		undefined,
		CollectionType.POPULAR,
		{
			providers: [props.provider.shortName],
		},
		providersByShortName.value,
		genresByShortName.value,
		subgenresList.value
	)
);

const backdropImage = computed(() => {
	if (!globalProviderSpotlightTitles.value.length) {
		return '';
	}
	return globalProviderSpotlightTitles.value[0].node.content.backdrops[0]?.backdropUrl || '';
});

const moduleContext = computed(() => new SnowplowOldModuleContext('', -1, props.moduleName, ''));

function handlePosterClick(title: any, index: number, contexts: SnowplowContext[] = []) {
	const titleContext = TrackingHelper.getTitleContextGraphql(
		title.node.objectId,
		title.node.objectType,
		title?.node?.content?.seasonNumber
	);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: 'featured',
			property: providerName.value,
			value: index,
		},
		[...contexts, titleContext, moduleContext.value]
	);
}

function handleSeeAllClick() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'content_click',
			label: providerName.value,
		},
		[moduleContext.value]
	);
}

const observeVisibilityOptions = computed(() => ({
	...defaultObservableOptions,
	callback: (isVisible: boolean) => {
		if (!isVisible) return;

		TrackingHelper.trackEvent(
			ImpressionTrackingEvents.MODULE,
			{
				action: 'impression',
				nonInteraction: true,
			},
			[moduleContext.value]
		);
	},
}));

return { __sfc: true,props, router, genresByShortName, subgenresList, providersByShortName, queryVariables, result, onError, globalProviderSpotlightTitles, providerName, providerUrl, backdropImage, moduleContext, handlePosterClick, handleSeeAllClick, observeVisibilityOptions, HiddenHorizontalScrollbar, PictureComp, ProviderIcon, PosterGraphql, TitleDetailLink }
}

})